import { supabase } from '../../lib/supabase';
import { workflowExecutionService } from '../../lib/services/workflowExecutionService';
import type { SavedActivity } from '../shared/activity-drawer/types';

/**
 * Scritture "nota" e side effect "workflow" del lead, estratti da `leadDrawerAdapter.ts`
 * per essere riusati TALI E QUALI dal composer inline della cronologia
 * (`shared/timeline/InlineComposer.tsx` via `useLeadComposerSaves.ts`).
 *
 * Estrazione puramente ADDITIVA: l'adapter del drawer continua a chiamare queste funzioni
 * con gli stessi argomenti di prima, quindi nessun call site cambia comportamento. Serve a
 * garantire che composer e drawer producano ESATTAMENTE le stesse righe e gli stessi
 * trigger, senza un secondo percorso di persistenza da tenere allineato.
 */

export interface LeadNoteToast {
  success(message: string): void;
  error(message: string): void;
}

export interface SaveLeadNoteParams {
  leadId: string;
  html: string;
  files: File[];
  pipelineContext?: string;
  createNote(entityId: string, content: string, pipelineContext?: string): Promise<{ id: string } | null>;
  uploadNoteAttachment(noteId: string, file: File): Promise<unknown>;
  toast: LeadNoteToast;
  /**
   * Colonna di collegamento della nota in `deal_notes` (CHECK a DB: esattamente una tra
   * `lead_id`/`deal_id`/`partner_id`). Assente = comportamento storico: `createNote`
   * riceve `leadId`. Per un partner passare `{ partner_id }` (Task 1, piano "Partner
   * drawer unificato"): `createNote` riceve quel valore al posto di `leadId` — `leadId`
   * resta comunque un campo richiesto per compatibilità di firma con i chiamanti lead
   * esistenti, ma viene ignorato quando `linkage.partner_id`/`linkage.lead_id` è presente.
   * Additivo: nessun chiamante esistente lo passa, quindi nessun comportamento cambia.
   */
  linkage?: { lead_id?: string; partner_id?: string };
}

/**
 * Fonte: `LeadStatusUpdateDrawer.tsx:1038-1066` (via `leadDrawerAdapter.saveNote`) — nota
 * con `pipelineContext`, upload degli allegati uno per uno con esito riassunto a toast e
 * toast finale "Nota aggiunta con successo" emesso anche in presenza di allegati.
 */
export async function saveLeadNoteWithAttachments({
  leadId,
  html,
  files,
  pipelineContext,
  createNote,
  uploadNoteAttachment,
  toast,
  linkage,
}: SaveLeadNoteParams): Promise<void> {
  const entityId = linkage?.partner_id ?? linkage?.lead_id ?? leadId;
  const createdNote = await createNote(entityId, html.trim(), pipelineContext);
  if (createdNote && files.length > 0) {
    try {
      let uploaded = 0;
      let failed = 0;
      for (const file of files) {
        const result = await uploadNoteAttachment(createdNote.id, file);
        if (result) uploaded++;
        else failed++;
      }
      if (failed === 0) {
        toast.success(`Nota salvata con ${uploaded} allegat${uploaded > 1 ? 'i' : 'o'}`);
      } else if (uploaded > 0) {
        toast.success(
          `Nota salvata: ${uploaded} allegat${uploaded > 1 ? 'i' : 'o'} caricat${uploaded > 1 ? 'i' : 'o'}, ${failed} fallit${failed > 1 ? 'i' : 'o'}`
        );
      } else {
        toast.error('Nota salvata ma nessun allegato è stato caricato');
      }
    } catch (error) {
      console.error('[leadDrawerWrites] upload allegati fallito:', error);
      toast.error('Nota salvata ma alcuni allegati non sono stati caricati');
    }
  }
  // Fonte :1697-1699 — toast emesso anche quando ci sono allegati.
  toast.success('Nota aggiunta con successo');
}

/**
 * Fonte: `LeadStatusUpdateDrawer.tsx:1415-1461` — i workflow di automazione scattano SOLO
 * alla creazione dell'attività e si biforcano su `client_states.is_lead` della riga
 * `clients` collegata. Best effort: qualunque errore resta un log.
 *
 * `kind` (Task 1, piano "Partner drawer unificato"): un partner non ha riga `clients` né
 * workflow di automazione collegati — la re-query su `clients` con `id = partnerId`
 * tornerebbe comunque `null` e la funzione uscirebbe da sola, ma qui evitiamo la
 * roundtrip inutile con un early-return esplicito. Default `'lead'` = comportamento
 * storico invariato per tutti i chiamanti esistenti (nessuno passa questo argomento).
 */
export async function triggerLeadActivityWorkflows(
  leadId: string,
  activity: SavedActivity,
  safeType: string,
  kind: 'lead' | 'partner' = 'lead'
): Promise<void> {
  if (kind === 'partner') return;
  try {
    const { data: entityData } = await supabase
      .from('clients')
      .select('pipeline_id, business_name, stato_cliente')
      .eq('id', leadId)
      .single();

    const { data: stateData } = await supabase
      .from('client_states')
      .select('is_lead')
      .eq('id', entityData?.stato_cliente)
      .maybeSingle();

    const payload = {
      title: activity.title,
      dueDate: activity.due_date,
      assignedTo: activity.assigned_to,
      businessName: entityData?.business_name,
    };

    if (stateData?.is_lead) {
      await workflowExecutionService.onLeadActivityCreated(
        leadId,
        activity.id,
        safeType,
        entityData?.pipeline_id,
        payload
      );
    } else {
      await workflowExecutionService.onClientActivityCreated(leadId, activity.id, safeType, payload);
    }
  } catch (workflowError) {
    console.error('[leadDrawerWrites] trigger workflow automation fallito:', workflowError);
  }
}
